import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import CustomBottomSheet from 'components/bottomSheet'
import {
    getNudgeSheet,
    INudgeGuarantor,
    sendNudge,
    TNudgeChannel,
    TNudgeTarget,
    TNudgeTone,
} from 'services/guarantees'
import { faPlain, toman } from 'utilities/helper/format'

export const CHANNEL_LABELS: Record<TNudgeChannel, string> = {
    bale: 'بله',
    eitaa: 'ایتا',
    telegram: 'تلگرام',
    sms: 'پیامک، از بستهٔ صندوق',
    app: 'فقط داخل همیان',
}

const TONE_LABELS: Record<TNudgeTone, { title: string; hint: string }> = {
    friendly: {
        title: 'یادآوری دوستانه',
        hint: '«از شما به‌عنوان ضامن خواسته پیگیری کنید…»',
    },
    serious: {
        title: 'پیگیری جدی',
        hint: '«پیگیری‌های صندوق تا الان نتیجه نداده…»',
    },
}

const typeLabel = (row: INudgeGuarantor) =>
    row.type === 'loan' ? 'ضامن وام' : 'ضامن عضویت، ضعیف‌تر'

const daysUntil = (iso: string | null) => {
    if (!iso) return 0
    const ms = new Date(iso).getTime() - Date.now()
    return Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)))
}

/**
 * «📣 پیگیری از ضامن‌ها» — a manager asks a late member's guarantors to follow
 * up (owner, 2026-09-24). The text is never the manager's: they pick one of
 * the predefined messages and who hears it. Shown only to managers, and only
 * while something is late.
 *
 * Loan → the loan's guarantors, ticked; the borrower's membership guarantor
 * listed apart as the weaker tie. حق عضویت → the membership guarantor. One
 * follow-up per loan (or member) every 3 days — the server enforces it; the
 * sheet says when the next one is possible.
 */
export default function GuarantorNudge({
    moneypoolId,
    target,
    isManager,
}: {
    moneypoolId: string | undefined
    target: TNudgeTarget | null
    isManager: boolean
}) {
    const [isOpen, setIsOpen] = useState(false)
    const queryClient = useQueryClient()
    const queryKey = ['guarantor-nudge', moneypoolId, target]
    const { data: sheet } = useQuery(
        queryKey,
        () => getNudgeSheet(moneypoolId as string, target as TNudgeTarget),
        { enabled: isManager && !!moneypoolId && !!target }
    )

    const [tone, setTone] = useState<TNudgeTone>('friendly')
    const [chosen, setChosen] = useState<Set<number>>(new Set())
    const [informMember, setInformMember] = useState(true)
    useEffect(() => {
        if (!sheet) return
        setChosen(
            new Set(
                sheet.guarantors
                    .filter((row) => row.checked)
                    .map((row) => row.poolship_id)
            )
        )
        setInformMember(sheet.inform_member)
    }, [sheet])

    const picked = useMemo(
        () =>
            (sheet?.guarantors ?? []).filter((row) =>
                chosen.has(row.poolship_id)
            ),
        [sheet, chosen]
    )

    const send = useMutation({
        mutationFn: () =>
            sendNudge(moneypoolId as string, target as TNudgeTarget, {
                tone,
                guarantor_ids: picked.map((row) => row.poolship_id),
                inform_member: informMember,
            }),
        onSuccess: () => {
            toast.success('پیگیری فرستاده شد')
            posthog.capture('guarantor_nudge_sent', {
                fund_id: Number(moneypoolId),
                subject: sheet?.subject,
                tone,
                guarantor_count: picked.length,
                membership_guarantors: picked.filter(
                    (row) => row.type === 'membership'
                ).length,
                channels: picked.map((row) => row.channel),
                informed_member: informMember,
            })
            setIsOpen(false)
            queryClient.invalidateQueries(queryKey)
        },
        onError: (error: any) => {
            const code = error?.response?.data?.results?.code
            toast.error(
                code === 'too_soon'
                    ? 'هر ۳ روز یک بار می‌شود از ضامن‌ها پیگیری کرد.'
                    : error?.response?.data?.params || 'پیگیری فرستاده نشد.'
            )
            queryClient.invalidateQueries(queryKey)
        },
    })

    if (!isManager || !sheet || sheet.late.count === 0) return null

    const isLoan = sheet.subject === 'loan'
    const last = sheet.history[0]
    const lateLine = isLoan
        ? `${faPlain.format(sheet.late.count)} قسط عقب افتاده، مجموعاً ${toman(
              sheet.late.total
          )}.`
        : `حق عضویت ${faPlain.format(
              sheet.late.count
          )} دوره پرداخت نشده، مجموعاً ${toman(sheet.late.total)}.`

    const toggle = (id: number) => {
        const next = new Set(chosen)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        setChosen(next)
    }

    const open = () => {
        setIsOpen(true)
        posthog.capture('guarantor_nudge_opened', {
            fund_id: Number(moneypoolId),
            subject: sheet.subject,
            can_send: sheet.can_send,
            reason: sheet.reason || null,
        })
    }

    return (
        <>
            <div className="mx-4 mt-3">
                <button
                    type="button"
                    onClick={open}
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-brand bg-white text-sm font-bold text-brand"
                >
                    📣 پیگیری از ضامن‌ها
                </button>
            </div>

            <CustomBottomSheet
                isOpen={isOpen}
                onDismiss={() => setIsOpen(false)}
                dragHandleOnly
            >
                <div className="px-4 pb-6 text-right" dir="rtl">
                    <p className="text-center text-base font-bold text-heading">
                        📣 پیگیری از ضامن‌ها
                    </p>
                    <p className="mt-3 rounded-lg bg-[#FFF4E5] p-3 text-xs leading-6 text-text-body">
                        ⏰ {lateLine}
                        {sheet.late.oldest_days > 0 &&
                            ` قدیمی‌ترین: ${faPlain.format(
                                sheet.late.oldest_days
                            )} روز پیش.`}
                    </p>

                    {sheet.reason === 'no_guarantor' ? (
                        <p className="mt-4 text-sm leading-7 text-text-body">
                            {isLoan
                                ? 'این وام ضامنی ندارد و وام‌گیرنده ضامن عضویت هم ندارد. ضامن وام را از بالای همین صفحه اضافه کنید.'
                                : 'این عضو ضامن عضویت ندارد.'}
                        </p>
                    ) : (
                        <>
                            {sheet.tones.length > 1 && (
                                <fieldset className="mt-4">
                                    <legend className="mb-2 text-sm font-bold text-text-body">
                                        کدام پیام برود؟
                                    </legend>
                                    {sheet.tones.map((option) => (
                                        <label
                                            key={option}
                                            className="mb-2 flex items-start gap-2 text-sm"
                                        >
                                            <input
                                                type="radio"
                                                name="nudge-tone"
                                                checked={tone === option}
                                                onChange={() => setTone(option)}
                                                className="mt-1.5"
                                            />
                                            <span>
                                                <b>
                                                    {TONE_LABELS[option].title}
                                                </b>
                                                <span className="block text-xs text-text-secondary">
                                                    {TONE_LABELS[option].hint}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </fieldset>
                            )}

                            <fieldset className="mt-4">
                                <legend className="mb-2 text-sm font-bold text-text-body">
                                    برای چه کسانی:
                                </legend>
                                {sheet.guarantors.map((row) => (
                                    <label
                                        key={row.poolship_id}
                                        className="mb-2 flex items-start gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={chosen.has(
                                                row.poolship_id
                                            )}
                                            onChange={() =>
                                                toggle(row.poolship_id)
                                            }
                                            className="mt-1.5"
                                            aria-label={row.name}
                                        />
                                        <span>
                                            <b>{row.name}</b>{' '}
                                            <span
                                                className={
                                                    row.type === 'loan'
                                                        ? 'text-text-body'
                                                        : 'text-text-secondary'
                                                }
                                            >
                                                {typeLabel(row)}
                                            </span>
                                            <span className="block text-xs text-text-secondary">
                                                {CHANNEL_LABELS[row.channel]}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                                <label className="mt-1 flex items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={informMember}
                                        onChange={(event) =>
                                            setInformMember(
                                                event.target.checked
                                            )
                                        }
                                        className="mt-1.5"
                                    />
                                    <span>
                                        به خود {sheet.member.name} هم پیام برود،
                                        با اسم ضامن‌ها
                                    </span>
                                </label>
                            </fieldset>
                        </>
                    )}

                    {last && (
                        <p className="mt-4 text-xs leading-6 text-text-secondary">
                            آخرین پیگیری:{' '}
                            {last.days_ago
                                ? `${faPlain.format(last.days_ago)} روز پیش`
                                : 'امروز'}
                            {last.by ? `، توسط ${last.by}` : ''}.
                            {sheet.reason === 'too_soon' &&
                                ` پیگیری بعدی تا ${faPlain.format(
                                    daysUntil(sheet.next_allowed_at)
                                )} روز دیگر ممکن نیست.`}
                        </p>
                    )}

                    <button
                        type="button"
                        disabled={
                            !sheet.can_send ||
                            picked.length === 0 ||
                            send.isLoading
                        }
                        onClick={() => send.mutate()}
                        className="mt-4 h-11 w-full rounded-xl bg-brand text-sm font-bold text-white disabled:opacity-40"
                    >
                        ارسال پیگیری
                    </button>
                </div>
            </CustomBottomSheet>
        </>
    )
}
